import { Notification } from './notifications';

export type NotificationPanel = 'admin' | 'agent';

type LinkTarget = Pick<Notification, 'type' | 'link' | 'referenceType' | 'referenceId'>;

/**
 * The backend stores a bare section slug in `notification.link` (e.g. 'orders',
 * 'listings') rather than a path, because the same notification can be read in
 * either panel and the two panels use different routes. These maps turn a slug
 * into the route that actually shows that content, per panel.
 *
 * A slug deliberately missing from a panel's map means that panel has no page
 * for it — the row then renders non-clickable instead of dropping the user on
 * an unrelated screen. The agent panel has no orders / revenue / reviews /
 * applications pages, so those slugs resolve to null there.
 */
const SECTIONS: Record<NotificationPanel, Record<string, string>> = {
  admin: {
    home: '/admin',
    listings: '/admin/listings',
    leads: '/admin/leads',
    inquiries: '/admin/leads',
    applications: '/admin/applications',
    agents: '/admin/agents',
    orders: '/admin/orders',
    revenue: '/admin/revenue',
    subscription: '/admin/subscriptions',
    subscriptions: '/admin/subscriptions',
    profile: '/admin/settings',
    settings: '/admin/settings',
  },
  agent: {
    home: '/agent',
    listings: '/agent/listings',
    inquiries: '/agent/inquiries',
    subscription: '/agent/subscription',
    profile: '/agent/settings',
    settings: '/agent/settings',
  },
};

/**
 * Reference types that have a detail route we can deep-link to. Everything
 * else falls back to its section list page.
 */
const DETAIL_ROUTES: Record<NotificationPanel, Record<string, (id: number) => string>> = {
  admin: {
    listing: (id) => `/admin/listings/${id}`,
    // Legal and maintenance share this type; the page redirects maintenance orders.
    service_order: (id) => `/admin/orders/${id}`,
    rental_application: (id) => `/admin/applications/${id}`,
  },
  agent: { listing: (id) => `/agent/listings/${id}` },
};

/**
 * Notifications about a user only have a detail page for some events: a new
 * merchant's page is worth opening, a welcome bell's reference is the reader.
 */
const TYPE_DETAIL_ROUTES: Record<NotificationPanel, Record<string, (id: number) => string>> = {
  admin: { 'agent.signup': (id) => `/admin/agents/${id}` },
  agent: {},
};

/**
 * Where a notification should take the reader, or null when this panel has
 * nowhere sensible to send them.
 */
export const resolveNotificationPath = (
  n: LinkTarget,
  panel: NotificationPanel
): string | null => {
  // Prefer the specific entity when this panel can show it on its own page.
  if (n.referenceType && n.referenceId != null) {
    const buildDetailPath = TYPE_DETAIL_ROUTES[panel][n.type] || DETAIL_ROUTES[panel][n.referenceType];
    if (buildDetailPath) return buildDetailPath(n.referenceId);
  }

  const link = (n.link || '').trim();
  if (!link) return null;

  /*
   * A couple of emitters store a ready-made path instead of a slug
   * (inquiry.created uses '/agent/inquiries'). Honour it only when it belongs
   * to the panel being viewed, so an admin-only path can never be handed to an
   * agent — ProtectedRoute would bounce them to a login screen.
   */
  if (link.startsWith('/')) {
    const prefix = `/${panel}`;
    return link === prefix || link.startsWith(`${prefix}/`) ? link : null;
  }

  return SECTIONS[panel][link] ?? null;
};
